import { ArrowBackOutlined, ListAlt } from "@mui/icons-material";
import Addicon from "@mui/icons-material/Add";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import {
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button as UIButton,
} from "@mui/material";
import React, { FC, useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { Controller, useFieldArray } from "react-hook-form";
import DeleteBin2LineIcon from "remixicon-react/DeleteBin2LineIcon";
import Upload2LineIcon from "remixicon-react/Upload2LineIcon";
import { addProduct } from "$api/productApi";
import { Button, Popin, Text } from "$components";
import JModal from "$components/JModal/JModal";
import JTooltip from "$components/JTooltip/JTooltip";
import ProductSearchInput, {
  getProductFieldMatch,
} from "$forms/ProductSearchInput";
import ProductSelect from "$forms/ProductSelect";
import useAuth from "$hooks/useAuth";
import useFilters from "$hooks/useFilters";
import useOrder from "$hooks/useOrder";
import { CompanyServices, ProductService } from "$services";
import { CacheInvalidationAction } from "$utils/cache";
import { queryClient } from "$utils/queryClient";
import ProductForm from "../../Products/ProductForm";
import ReferenceCSVReader from "../CSVReader/ReferenceCSVReader";

const HAS_RECEPTION_MESSAGE = "Cette commande a déjà des réceptions.";

type Props = {
  isReadOnly?: boolean;
  nestIndex: any;
  control: any;
  register: any;
  getValues: any;
  setValue: any;
  errors: any;
  watch: any;
  currentCompany: string;
  filters: any;
  setFilter: (name: any, value: any) => void;
  order?: any;
  // products?: any;
  clearErrors: any;
  receptions?: any;
  index: number;
  setIsSubFilterUsed: any;
  subordersWithEdbs: number[];
  subordersWithReceptions: string[];
};

const ProductsInSubordersForm: FC<Props> = ({
  isReadOnly = false,
  nestIndex,
  control,
  register,
  getValues,
  setValue,
  errors,
  currentCompany,
  filters,
  setFilter,
  watch,
  clearErrors,
  receptions,
  order,
  index,
  setIsSubFilterUsed,
  subordersWithEdbs,
  subordersWithReceptions,
}) => {
  const { classes } = useOrder();
  const [sorted, setSorted] = useState(false);
  const [productError, setProductError] = useState<any>(null);
  const { isAdmin, token } = useAuth();
  const { fields, remove, append } = useFieldArray({
    control,
    name: `suborders[${nestIndex}].suborderProducts`,
  });
  const { filters: subFilters, setFilter: setSubFilters } = useFilters(
    `order-form-${index}`
  );

  // Helper function to check if the current suborder is linked to an EDB
  const isCurrentSuborderLinkedToEdb = (): boolean => {
    const currentSuborderId = getValues(`suborders[${nestIndex}].id`);
    if (!currentSuborderId) return false;
    return subordersWithEdbs.includes(currentSuborderId);
  };

  // Helper function to check if the current suborder has receptions
  const hasCurrentSuborderReception = (): boolean => {
    const currentSuborderRef = getValues(`suborders[${nestIndex}].reference`);
    if (!currentSuborderRef) return false;
    return subordersWithReceptions.includes(currentSuborderRef);
  };

  // Message to display when suborder is linked to an EDB
  const HAS_EDB_LINKED_MESSAGE =
    "Cette sous-commande est intégrée dans un EDB et ne peut pas être modifiée.";

  useEffect(() => {
    if (subFilters.reference || subFilters.designation) {
      setIsSubFilterUsed(true);
    } else {
      setIsSubFilterUsed(false);
    }
  }, [setIsSubFilterUsed, subFilters.designation, subFilters.reference]);

  const exportTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["Référence", "Quantité"],
      ["", ""],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "Template import références.xlsx");
  };

  const [showImportModal, setShowImportModal] = useState(false);

  const handleAddRefList = (datas: any) => {
    const currentProducts =
      getValues(`suborders[${nestIndex}].suborderProducts`) || [];

    for (let i = currentProducts.length - 1; i >= 0; i--) {
      const currentProduct = currentProducts[i];

      const isEmpty =
        (!currentProduct?.reference || currentProduct.reference === "") &&
        (!currentProduct?.name || currentProduct.name === "") &&
        (!currentProduct?.unit || currentProduct.unit === "") &&
        (!currentProduct?.quantity ||
          currentProduct.quantity === "" ||
          currentProduct.quantity === 0);

      if (isEmpty) {
        remove(i);
      }
    }

    const updatedCurrentProducts =
      getValues(`suborders[${nestIndex}].suborderProducts`) || [];

    datas.forEach((importedData: any) => {
      const importedReference = importedData.reference;
      const importedQuantity = importedData.quantity;
      const importedName = importedData.name || importedData.designation || "";
      const importedUnit = importedData.unit || "";

      const existingIndex = updatedCurrentProducts.findIndex(
        (product: any) => product.reference === importedReference
      );

      if (existingIndex >= 0) {
        setValue(
          `suborders[${nestIndex}].suborderProducts[${existingIndex}].quantity`,
          importedQuantity
        );
      } else {
        append({
          reference: importedReference,
          quantity: importedQuantity,
          name: importedName,
          unit: importedUnit,
        });
      }
    });

    setShowImportModal(false);
  };

  /* add product */
  const [productFilterCompany, setProductFilterCompany] = useState(null);
  const [openProductForm, toggleOpenProductForm] = useState<boolean>(false);
  const [productFieldLine, setProductFieldLine] =
    React.useState<any>(undefined);
  const [company, setCompany] = useState(null);
  const [prefilledReference, setPrefilledReference] = useState<string>("");

  // get company
  const companyById = useCallback(async () => {
    if (currentCompany) {
      let company;
      company = await queryClient.getQueryData([
        CacheInvalidationAction.GET_COMPANY_BY_ID,
        currentCompany,
      ]);
      if (!company) {
        company = await queryClient.fetchQuery({
          queryKey: [CacheInvalidationAction.GET_COMPANY_BY_ID, currentCompany],
          queryFn: () => CompanyServices.getCompanyById(currentCompany),
          gcTime: 60 * 1000,
        });
        await queryClient.setQueryData(
          [CacheInvalidationAction.GET_COMPANY_BY_ID, currentCompany],
          company
        );
      }
      setCompany(company);
    }
  }, [setCompany, currentCompany]);

  useEffect(() => {
    if (currentCompany) {
      companyById();
      setProductFilterCompany((oldProductFilterCompany) => {
        if (fields) {
          if (
            oldProductFilterCompany?._id &&
            oldProductFilterCompany._id !== company?._id
          ) {
            for (let i = fields.length - 1; i >= 0; i -= 1) {
              remove(i);
            }
            append("");
          }
        }
        return company;
      });
    }
  }, [companyById, append, currentCompany, fields, remove, company]);

  const getCompany = async (id) => {
    try {
      const company = await queryClient.fetchQuery({
        queryKey: [CacheInvalidationAction.GET_COMPANY_BY_ID, id],
        queryFn: () => CompanyServices.getCompanyById(id),
        staleTime: 1000 * 60,
      });
      return company;
    } catch {
      return null;
    }
  };

  const onProductSubmit = async (data: any) => {
    try {
      const res: any = await addProduct(token, {
        ...data,
        isReferenced: true,
      });
      if (res?.error) {
        let msg = res.error || "";
        if (msg.includes("exist")) {
          const comp = await getCompany(data?.company);
          msg = `Un produit avec la même référence produit existe déjà dans le catalogue${
            comp ? ` de ${comp?.name}` : ""
          }, veuillez modifier la référence principale ou annuler la création du produit`;
        }
        setProductError(msg);
      } else {
        // TODO j'ai retirer ça, à priori aucun intérêt, je laisse en commentaire exprès
        // await getProducts(true);
        toggleOpenProductForm(false);
        setPrefilledReference("");

        // Get the created product from the response (if available)
        // If not available, construct it from the form data
        const createdProduct =
          res?.data?.data ||
          res?.data?.product ||
          (data.reference
            ? {
                reference: data.reference,
                designation: data.designation,
                unit: data.unit,
                company: data.company,
                ...data,
              }
            : null);

        // Manually fetch and cache the product so ProductSelect can find it immediately
        // This is necessary because SearchSelect's init only runs once on mount
        if (data.reference && currentCompany) {
          try {
            // Fetch the product using the same service that ProductSelect uses
            const productData = await ProductService.getProductByReference({
              $inreference: [data.reference],
            });

            // Cache it with the correct key structure that SearchSelect expects
            // SearchSelect init looks for cache with GET_PRODUCTS key and $inreference filter
            const sortedRefArray = [data.reference].sort();
            const cacheKey = [
              CacheInvalidationAction.GET_PRODUCTS,
              { $inreference: sortedRefArray },
            ];

            // SearchSelect has a "Special case for products" that handles arrays directly
            // So we store as an array, not { data: [...] }
            let productsArray: any[] = [];
            if (Array.isArray(productData)) {
              productsArray = productData;
            } else if (productData?.data && Array.isArray(productData.data)) {
              productsArray = productData.data;
            } else if (createdProduct) {
              // Fallback: use the created product from form data
              productsArray = [createdProduct];
            }

            if (productsArray.length > 0) {
              queryClient.setQueryData(cacheKey, productsArray);
            }
          } catch {
            // If fetch fails, still try to cache the constructed product
            if (createdProduct) {
              const sortedRefArray = [data.reference].sort();
              const cacheKey = [
                CacheInvalidationAction.GET_PRODUCTS,
                { $inreference: sortedRefArray },
              ];
              queryClient.setQueryData(cacheKey, [createdProduct]);
            }
          }
        }

        // Fill the form line with the newly created product immediately
        if (productFieldLine !== undefined) {
          const currentProducts = getValues(
            `suborders[${nestIndex}].suborderProducts`
          );
          const lineIndex = productFieldLine;

          // Ensure the line exists - if it doesn't (shouldn't happen, but safety check), append it
          if (!currentProducts || currentProducts.length <= lineIndex) {
            // Line doesn't exist yet, append it with the product data
            append({
              reference: data.reference,
              name: data.designation,
              unit: data.unit,
              quantity: "",
            });
          } else {
            // Line exists, update it with the product data
            // Set all fields at once for better performance
            setValue(
              `suborders[${nestIndex}].suborderProducts[${lineIndex}]`,
              {
                reference: data.reference,
                name: data.designation,
                unit: data.unit,
                quantity:
                  getValues(
                    `suborders[${nestIndex}].suborderProducts[${lineIndex}].quantity`
                  ) || "",
              },
              { shouldValidate: true, shouldDirty: true }
            );
          }

          // Clear any errors for this field
          clearErrors(
            `suborders[${nestIndex}].suborderProducts[${lineIndex}].reference`
          );
          setProductFieldLine(undefined);
        }

        // Invalidate product queries to ensure fresh data
        queryClient.invalidateQueries({
          queryKey: [CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE],
        });
        queryClient.invalidateQueries({
          queryKey: [CacheInvalidationAction.GET_PRODUCTS],
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log("error =>", err);
    }
  };

  const removeEmptyLines = () => {
    if (fields.length > 1) {
      fields.map((field, index) => {
        if (
          ((field as any).reference === undefined ||
            (field as any).reference === "") &&
          ((field as any).name === undefined || (field as any).name === "") &&
          ((field as any).unit === undefined || (field as any).unit === "") &&
          ((field as any).quantity === undefined ||
            (field as any).quantity === "") &&
          fields.length > 1
        ) {
          remove(index);
        }
        return null;
      });
    }
  };

  const initialSelectedProducts = getValues(
    `suborders[${nestIndex}].suborderProducts`
  );
  const currentSelectedProducts = watch(
    `suborders[${nestIndex}].suborderProducts`,
    initialSelectedProducts
  );

  const [isFilteredByProducts, setIsFilteredByProducts] = useState(false);

  useEffect(() => {
    if (
      (subFilters.designation && subFilters.designation !== "") ||
      (subFilters.reference && subFilters.reference !== "") ||
      (filters.product && filters.product !== "")
    ) {
      setIsFilteredByProducts(true);
    } else {
      setIsFilteredByProducts(false);
    }
  }, [
    setIsFilteredByProducts,
    filters.designation,
    filters.reference,
    filters.product,
    subFilters.designation,
    subFilters.reference,
  ]);
  // receptions products
  const getProductQuantity = (
    orderRef: string,
    suborderRef: string,
    productRef: string
  ) => {
    if (!receptions || !Array.isArray(receptions) || !orderRef) {
      return {
        receivedQuantity: 0,
        conformity: true,
        unconformQuantity: 0,
      };
    }

    const currentReceptions = receptions.filter((reception: any) => {
      // Check if reception and productQuantity exist before using find
      return (
        reception &&
        reception.productQuantity &&
        Array.isArray(reception.productQuantity) &&
        reception.productQuantity.some(
          (product: any) => product && product.orderReference === orderRef
        )
      );
    });

    const currentProduct = {
      receivedQuantity: 0,
      conformity: true,
      unconformQuantity: 0,
    };

    currentReceptions.forEach((cR: any) => {
      if (cR && cR.productQuantity && Array.isArray(cR.productQuantity)) {
        const cP = cR.productQuantity.find(
          (product: any) =>
            product &&
            product.suborderReference === suborderRef &&
            product.productId === productRef
        );

        if (cP) {
          currentProduct.receivedQuantity += cP.receivedQuantity || 0;
          if (currentProduct.conformity) {
            currentProduct.conformity = cP.conformity !== false;
          }
          currentProduct.unconformQuantity += cP.unconformQuantity || 0;
        }
      }
    });

    return currentProduct;
  };

  const matchToProductFilters = (indexSuborder, indexProduct) => {
    if (filters.product) {
      return (
        getProductFieldMatch(
          getValues(
            `suborders[${indexSuborder}].suborderProducts[${indexProduct}].reference`
          ),
          filters.product
        ) ||
        getProductFieldMatch(
          getValues(
            `suborders[${indexSuborder}].suborderProducts[${indexProduct}].name`
          ),
          filters.product
        )
      );
    }

    if (subFilters.reference && subFilters.designation) {
      return (
        getProductFieldMatch(
          getValues(
            `suborders[${indexSuborder}].suborderProducts[${indexProduct}].reference`
          ),
          subFilters.reference
        ) &&
        getProductFieldMatch(
          getValues(
            `suborders[${indexSuborder}].suborderProducts[${indexProduct}].name`
          ),
          subFilters.designation
        )
      );
    }

    if (subFilters.reference) {
      return getProductFieldMatch(
        getValues(
          `suborders[${indexSuborder}].suborderProducts[${indexProduct}].reference`
        ),
        subFilters.reference
      );
    }

    return getProductFieldMatch(
      getValues(
        `suborders[${indexSuborder}].suborderProducts[${indexProduct}].name`
      ),
      subFilters.designation
    );
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          width: "100%",
          justifyContent: "flex-start",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Switch
          checked={sorted}
          onChange={() => {
            setSorted(!sorted);
          }}
          name="showCompany"
          color="primary"
        />
        <Text type="text">Trier par désignation</Text>
      </div>
      <TableContainer>
        <Table size="small">
          <TableHead className={classes.topFormTable}>
            <TableRow>
              <TableCell padding="none" style={{ minWidth: 220 }}>
                <div className={classes.productsTableCellHead}>
                  <div style={{ width: "100%" }}>
                    <ProductSearchInput
                      value={subFilters.reference}
                      onChange={(e: any) => {
                        setSubFilters("reference", e.target.value);
                      }}
                      mode="reference"
                      placeholder="Rechercher par référence"
                      disabled={filters.product?.length > 0}
                    />
                  </div>
                </div>
              </TableCell>
              <TableCell padding="none" style={{ minWidth: 280 }}>
                <div className={classes.productsTableCellHead}>
                  <div style={{ width: "100%" }}>
                    <ProductSearchInput
                      value={subFilters.designation}
                      onChange={(e: any) => {
                        setSubFilters("designation", e.target.value);
                      }}
                      mode="designation"
                      placeholder="Rechercher par désignation"
                      disabled={filters.product?.length > 0}
                    />
                  </div>
                </div>
              </TableCell>
              <TableCell padding="none">
                <div className={classes.productsTableCellHead}>Unités</div>
              </TableCell>
              <TableCell padding="none" style={{ width: 100 }}>
                <div className={classes.productsTableCellHead}>Qté</div>
              </TableCell>

              {/* receptions */}
              {hasCurrentSuborderReception() && (
                <>
                  <TableCell padding="none" style={{ width: 80 }}>
                    <div className={classes.productsTableCellHead}>
                      Qté <small style={{ marginTop: 4 }}>reçue</small>
                    </div>
                  </TableCell>

                  <TableCell padding="none">
                    <div className={classes.productsTableCellHead}>
                      Conformité
                    </div>
                  </TableCell>
                </>
              )}
              <TableCell padding="none" style={{ minWidth: 50 }} />
            </TableRow>
          </TableHead>

          <TableBody className={classes.bodyFormTable}>
            {isFilteredByProducts &&
              !fields.find((_item, k) =>
                matchToProductFilters(nestIndex, k)
              ) && (
                <TableRow>
                  <TableCell padding="none" colSpan={6}>
                    <div style={{ textAlign: "center", padding: 20 }}>
                      Aucun produit correspondant.
                    </div>
                  </TableCell>
                </TableRow>
              )}

            {[...fields]
              .sort((a: any, b: any) => {
                if (!sorted) return 0;

                if (!a.name && !b.name) return 0;
                if (!a.name) return 1;
                if (!b.name) return -1;

                return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
              })
              .map((item, k) => {
                const initialName = getValues(
                  `suborders[${nestIndex}].suborderProducts[${k}].name`
                );
                const currentName = watch(
                  `suborders[${nestIndex}].suborderProducts[${k}].name`,
                  initialName
                );

                return !isFilteredByProducts ||
                  matchToProductFilters(nestIndex, k) ? (
                  <TableRow key={item.id}>
                    <TableCell padding="none">
                      <Controller
                        control={control}
                        name={`suborders[${nestIndex}].suborderProducts[${k}].reference`}
                        {...register(
                          `suborders[${nestIndex}].suborderProducts[${k}].reference`
                        )}
                        rules={{
                          required: "Veuillez remplir ce champ",
                          validate: {
                            value: (value) =>
                              currentSelectedProducts.filter(
                                (cp: any) => cp.reference === value
                              )?.length < 2,
                          },
                        }}
                        render={({ field: { onChange, value } }) => (
                          <div style={{ flex: 1 }}>
                            <JTooltip
                              title={
                                (hasCurrentSuborderReception() &&
                                  HAS_RECEPTION_MESSAGE) ||
                                (isCurrentSuborderLinkedToEdb() &&
                                  HAS_EDB_LINKED_MESSAGE)
                              }
                            >
                              {/* ATTENTION : Ce selecteur de produits marche par REFERENCE ! et non par _id comme les autres select !  */}
                              <ProductSelect
                                data-testid="order-input-search"
                                getProductLabel={(opt) => opt?.reference}
                                value={value}
                                placeholder="Sélectionnez un produit"
                                companyId={company?._id}
                                disabled={
                                  !currentCompany ||
                                  !isAdmin() ||
                                  hasCurrentSuborderReception() ||
                                  isReadOnly ||
                                  isCurrentSuborderLinkedToEdb()
                                }
                                disableClearable
                                valueKey="reference"
                                queryFieldForInit="$inreference"
                                createLabel="Créer le produit"
                                isActive
                                onChange={(e, data) => {
                                  // add product if unexist
                                  if (data?.inputValue) {
                                    setPrefilledReference(data.reference);
                                    toggleOpenProductForm(true);

                                    // reset la ligne
                                    remove(k);

                                    // future position du champs
                                    const newIndex = getValues(
                                      `suborders[${nestIndex}].suborderProducts`
                                    ).length;
                                    setProductFieldLine(newIndex);
                                    append({
                                      reference: "",
                                    });
                                  } else {
                                    onChange(data?.reference);
                                    // set designation automatically
                                    setValue(
                                      `suborders[${nestIndex}].suborderProducts[${k}].name`,
                                      data?.designation
                                    );
                                    // set unit automatically
                                    setValue(
                                      `suborders[${nestIndex}].suborderProducts[${k}].unit`,
                                      data?.unit
                                    );
                                  }
                                }}
                              />
                            </JTooltip>
                          </div>
                        )}
                      />

                      {!currentCompany && (
                        <Text style={{ fontStyle: "italic", fontSize: 10 }}>
                          Veuillez sélectionner une entreprise
                        </Text>
                      )}
                    </TableCell>

                    <TableCell padding="none">
                      <div title={currentName} style={{ width: "100%" }}>
                        <Controller
                          control={control}
                          name={`suborders[${nestIndex}].suborderProducts[${k}].name`}
                          {...register(
                            `suborders[${nestIndex}].suborderProducts[${k}].name`
                          )}
                          render={({ field }) => (
                            <TextField
                              style={{ width: "100%" }}
                              variant="outlined"
                              disabled
                              {...field}
                              error={
                                errors.suborders !== undefined &&
                                errors.suborders[nestIndex]
                                  ?.suborderProducts !== undefined &&
                                errors.suborders[nestIndex]?.suborderProducts[k]
                                  ?.name !== undefined
                              }
                            />
                          )}
                        />
                      </div>
                    </TableCell>

                    <TableCell padding="none">
                      <Controller
                        control={control}
                        name={`suborders[${nestIndex}].suborderProducts[${k}].unit`}
                        {...register(
                          `suborders[${nestIndex}].suborderProducts[${k}].unit`
                        )}
                        render={({ field }) => (
                          <TextField
                            variant="outlined"
                            disabled
                            {...field}
                            error={
                              errors.suborders !== undefined &&
                              errors.suborders[nestIndex]?.suborderProducts !==
                                undefined &&
                              errors.suborders[nestIndex]?.suborderProducts[k]
                                ?.unit !== undefined
                            }
                          />
                        )}
                      />
                    </TableCell>

                    <TableCell padding="none">
                      <Controller
                        control={control}
                        name={`suborders[${nestIndex}].suborderProducts[${k}].quantity`}
                        {...register(
                          `suborders[${nestIndex}].suborderProducts[${k}].quantity`
                        )}
                        rules={{
                          required: "Veuillez remplir ce champ",
                        }}
                        render={({ field }) => (
                          <JTooltip
                            title={
                              (hasCurrentSuborderReception() &&
                                HAS_RECEPTION_MESSAGE) ||
                              (isCurrentSuborderLinkedToEdb() &&
                                HAS_EDB_LINKED_MESSAGE)
                            }
                          >
                            <TextField
                              variant="outlined"
                              {...field}
                              error={
                                errors.suborders !== undefined &&
                                errors.suborders[nestIndex]
                                  ?.suborderProducts !== undefined &&
                                errors.suborders[nestIndex]?.suborderProducts[k]
                                  ?.quantity !== undefined
                              }
                              disabled={
                                !isAdmin() ||
                                hasCurrentSuborderReception() ||
                                isReadOnly ||
                                isCurrentSuborderLinkedToEdb()
                              }
                              autoComplete="off"
                            />
                          </JTooltip>
                        )}
                      />
                    </TableCell>

                    {/* receptions */}
                    {hasCurrentSuborderReception() && (
                      <>
                        <TableCell padding="none">
                          <Controller
                            control={control}
                            name={`suborders[${nestIndex}].suborderProducts[${k}].reference`}
                            {...register(
                              `suborders[${nestIndex}].suborderProducts[${k}].reference`
                            )}
                            render={({ field }) => (
                              <TextField
                                disabled
                                variant="outlined"
                                value={
                                  receptions.length && order
                                    ? (getProductQuantity(
                                        order?.reference,
                                        getValues(
                                          `suborders[${nestIndex}].reference`
                                        ),
                                        field.value
                                      ).receivedQuantity ?? 0)
                                    : 0
                                }
                              />
                            )}
                          />
                        </TableCell>

                        <TableCell padding="none">
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                            }}
                          >
                            <Controller
                              control={control}
                              name={`suborders[${nestIndex}].suborderProducts[${k}].reference`}
                              {...register(
                                `suborders[${nestIndex}].suborderProducts[${k}].reference`
                              )}
                              render={({ field }) => (
                                <Button
                                  color="primary"
                                  style={{ minWidth: 40, marginRight: 8 }}
                                  disabled
                                  onClick={() => {}}
                                >
                                  {receptions.length && order ? (
                                    getProductQuantity(
                                      order?.reference,
                                      getValues(
                                        `suborders[${nestIndex}].reference`
                                      ),
                                      field.value
                                    )?.conformity === false ? (
                                      <div style={{ color: "tomato" }}>
                                        <SentimentVeryDissatisfiedIcon />
                                      </div>
                                    ) : (
                                      <div style={{ color: "green" }}>
                                        <SentimentSatisfiedAltIcon />
                                      </div>
                                    )
                                  ) : (
                                    <div style={{ color: "green" }}>
                                      <SentimentSatisfiedAltIcon />
                                    </div>
                                  )}
                                </Button>
                              )}
                            />

                            <Controller
                              control={control}
                              name={`suborders[${nestIndex}].suborderProducts[${k}].reference`}
                              {...register(
                                `suborders[${nestIndex}].suborderProducts[${k}].reference`
                              )}
                              render={({ field }) => {
                                return receptions.length &&
                                  order &&
                                  getProductQuantity(
                                    order?.reference,
                                    getValues(
                                      `suborders[${nestIndex}].reference`
                                    ),
                                    field.value
                                  ).unconformQuantity ? (
                                  <TextField
                                    variant="outlined"
                                    value={
                                      receptions.length && order
                                        ? (getProductQuantity(
                                            order?.reference,
                                            getValues(
                                              `suborders[${nestIndex}].reference`
                                            ),
                                            field.value
                                          ).unconformQuantity ?? 0)
                                        : 0
                                    }
                                    disabled
                                  />
                                ) : null;
                              }}
                            />
                          </div>
                        </TableCell>
                      </>
                    )}

                    {isAdmin() && (
                      <TableCell padding="none">
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "flex-end",
                          }}
                        >
                          <UIButton
                            variant="text"
                            style={{
                              textTransform: "none",
                              minWidth: 20,
                            }}
                            onClick={() => {
                              remove(k);
                              setFilter("designation", "");
                              setFilter("reference", "");
                            }}
                            className={classes.deleteButton}
                            disabled={
                              hasCurrentSuborderReception() ||
                              isReadOnly ||
                              isCurrentSuborderLinkedToEdb()
                            }
                          >
                            <DeleteBin2LineIcon />
                          </UIButton>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ) : null;
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {isAdmin() && !isReadOnly && (
        <div
          style={{
            marginTop: 15,
            display: "flex",
            alignItems: "center",
            gap: "32px",
          }}
        >
          {/* Ajouter une ligne button */}
          <Button
            variant="text"
            style={{
              padding: 0,
              textTransform: "none",
              fontWeight: "normal",
              fontSize: "14px",
              border: "none",
              color: "black",
              boxShadow: "none",
              backgroundColor: "transparent",
              minWidth: "auto",
            }}
            onClick={() => {
              append({
                reference: "",
              });
              setFilter("designation", "");
              setFilter("reference", "");
            }}
            disabled={hasCurrentSuborderReception()}
            startIcon={<Addicon style={{ fontSize: 18, color: "black" }} />}
          >
            Ajouter une ligne
          </Button>

          {/* Ajouter une liste de références button */}
          <Button
            variant="text"
            style={{
              padding: 0,
              textTransform: "none",
              fontWeight: "normal",
              fontSize: "14px",
              border: "none",
              color: "black",
              boxShadow: "none",
              backgroundColor: "transparent",
              minWidth: "auto",
            }}
            onClick={() => {
              setShowImportModal(true);
              setFilter("designation", "");
              setFilter("reference", "");
            }}
            disabled={currentCompany === null || hasCurrentSuborderReception()}
            startIcon={<ListAlt style={{ fontSize: 18, color: "black" }} />}
          >
            Ajouter une liste de références
          </Button>

          {/* Créer un produit button */}
          {currentCompany && (
            <Button
              variant="text"
              style={{
                padding: 0,
                textTransform: "none",
                fontWeight: "normal",
                fontSize: "14px",
                border: "none",
                color: "black",
                boxShadow: "none",
                backgroundColor: "transparent",
                minWidth: "auto",
              }}
              onClick={() => {
                setFilter("designation", "");
                setFilter("reference", "");
                toggleOpenProductForm(true);

                const replaceFirstLine =
                  fields.length === 1 &&
                  getValues(
                    `suborders[${nestIndex}].suborderProducts[0].reference`
                  ) === "";

                setProductFieldLine(
                  replaceFirstLine
                    ? 0
                    : getValues(`suborders[${nestIndex}].suborderProducts`)
                        .length
                );

                if (!replaceFirstLine) {
                  append({
                    reference: "",
                  });
                }
              }}
              disabled={hasCurrentSuborderReception()}
              startIcon={<Addicon style={{ fontSize: 18, color: "black" }} />}
            >
              Créer un produit
            </Button>
          )}
        </div>
      )}

      {showImportModal && (
        <JModal hideConfirm hideClose onClose={() => setShowImportModal(false)}>
          <>
            <div
              className="buttons"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
              }}
            >
              <Button onClick={() => setShowImportModal(false)} variant="text">
                <ArrowBackOutlined style={{ marginRight: 8 }} />
                Retour
              </Button>

              <Button onClick={exportTemplate} variant="text">
                <Upload2LineIcon style={{ marginRight: 8 }} />
                Télécharger un modèle
              </Button>
            </div>

            <ReferenceCSVReader
              onConfirmAddRefList={handleAddRefList}
              currentCompany={currentCompany}
              alreadySelectedProducts={currentSelectedProducts}
            />
          </>
        </JModal>
      )}

      <Popin
        openPopin={openProductForm}
        toggleDrawer={toggleOpenProductForm}
        customCloseAction={() => {
          toggleOpenProductForm(false);
          setProductError(undefined);
          setPrefilledReference("");
          removeEmptyLines();
        }}
      >
        <ProductForm
          key={`product-form-${prefilledReference || "empty"}`}
          onSubmit={onProductSubmit}
          onReset={() => {
            toggleOpenProductForm(false);
            setProductError(undefined);
            setPrefilledReference("");
          }}
          company={productFilterCompany}
          productError={productError}
          defaultValuesForm={{ reference: prefilledReference }}
        />
      </Popin>
    </>
  );
};

export default ProductsInSubordersForm;
